import { useState, useRef, useEffect, useCallback } from "react";
import {
  Tabs,
  Tab,
  Button,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

// ─── Toolbar Button ───────────────────────────────────────────────────────────

function FmtBtn({ onClick, title, children, active }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{title}</Tooltip>}>
      <Button
        variant={active ? "primary" : "outline-secondary"}
        size="sm"
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
        className="me-1 mb-1 px-2 py-1"
        style={{ minWidth: 30, fontSize: 13 }}
      >
        {children}
      </Button>
    </OverlayTrigger>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * EmailTemplateEditor
 *
 * Props:
 *  - subject   {string}   Initial email subject
 *  - content   {string}   Initial HTML body content
 *  - onSubmit  {function} Called with { subject: string, html: string }
 *                         where `html` is the full HTML document string
 */
export default function UpdateAutoEmailsAndResendEditor({
  subject: initialSubject = "",
  content: initialContent = "",
  onSubmit,
  isLoading,
}) {
  const [activeTab, setActiveTab] = useState("editor");
  const [subject, setSubject] = useState(initialSubject);
  const [activeFormats, setActiveFormats] = useState({});

  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const initializedRef = useRef(false);

  // ── Init editor once ────────────────────────────────────────────────────
  const initEditor = useCallback(
    (node) => {
      if (node && !initializedRef.current) {
        initializedRef.current = true;
        editorRef.current = node;
        node.innerHTML = initialContent;
      }
    },
    [initialContent],
  );

  // ── Sync content if prop changes after mount ────────────────────────────
  useEffect(() => {
    if (editorRef.current && initialContent && !initializedRef.current) {
      editorRef.current.innerHTML = initialContent;
    }
  }, [initialContent]);

  // ── Sync subject if prop changes ────────────────────────────────────────
  useEffect(() => {
    setSubject(initialSubject);
  }, [initialSubject]);

  // ── Toolbar format states ───────────────────────────────────────────────
  const syncFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
      justifyLeft: document.queryCommandState("justifyLeft"),
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight: document.queryCommandState("justifyRight"),
    });
  };

  // ── Selection helpers ───────────────────────────────────────────────────
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
    syncFormats();
  };

  // ── execCommand ─────────────────────────────────────────────────────────
  const execCmd = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    syncFormats();
  };

  // ── Build final HTML document string ────────────────────────────────────
  const buildHtmlString = () => {
    const body = editorRef.current ? editorRef.current.innerHTML : "";
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${subject}</title>
  <style>
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #111;
      margin: 0;
      padding: 0;
      background: #f6f6f6;
    }
    .email-wrap {
      max-width: 600px;
      margin: 2rem auto;
      background: #fff;
      border-radius: 6px;
      overflow: hidden;
    }
    .email-header {
      padding: 24px 32px 16px;
      border-bottom: 2px solid #2563eb;
      background: #f4f5f9;
    }
    .email-subject {
      font-size: 1.2rem;
      font-weight: bold;
      color: #111;
      margin: 0;
    }
    .email-body {
      padding: 24px 32px;
      font-size: 14px;
      line-height: 1.75;
    }
    .email-body p { margin-bottom: 0.75rem; }
    .email-body a { color: #2563eb; text-decoration: underline; }
    .email-body ul { padding-left: 1.4rem; list-style: disc; margin-bottom: 0.75rem; }
    .email-body ol { padding-left: 1.4rem; list-style: decimal; margin-bottom: 0.75rem; }
    .email-body h1 { font-size: 1.5rem; font-weight: bold; margin-bottom: 0.5rem; }
    .email-body h2 { font-size: 1.2rem; font-weight: bold; margin-bottom: 0.5rem; }
    .email-body h3 { font-size: 1rem; font-weight: bold; margin-bottom: 0.5rem; }
    .email-body blockquote {
      border-left: 3px solid #d1d5db;
      padding-left: 1rem;
      color: #6b7280;
      margin: 0.75rem 0;
      font-style: italic;
    }
    .email-footer {
      padding: 12px 32px;
      background: #dce9f1;
      text-align: center;
      font-size: 11px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="email-wrap">
   
    <div class="email-body">
      ${body}
    </div>
  </div>
</body>
</html>`;
  };

  // ── Handle submit ───────────────────────────────────────────────────────
  const handleSubmit = () => {
    const html = buildHtmlString();
    if (onSubmit) {
      onSubmit({ subject, html });
    }
  };

  // ── Preview rendered html ────────────────────────────────────────────────
  const getPreviewHtml = () => {
    return editorRef.current ? editorRef.current.innerHTML : "";
  };

  return (
    <>
      <style>{`
        #ete-editor p          { margin-bottom: 0.75rem; }
        #ete-editor h1         { font-size: 1.6rem; font-weight: 700; margin-bottom: 0.5rem; }
        #ete-editor h2         { font-size: 1.3rem; font-weight: 700; margin-bottom: 0.5rem; }
        #ete-editor h3         { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; }
        #ete-editor blockquote { border-left: 3px solid #d1d5db; padding-left: 1rem; color: #6b7280; margin: 0.75rem 0; font-style: italic; }
        #ete-editor ul         { padding-left: 1.5rem; list-style: disc; margin-bottom: 0.75rem; }
        #ete-editor ol         { padding-left: 1.5rem; list-style: decimal; margin-bottom: 0.75rem; }
        #ete-editor a          { color: #2563eb; text-decoration: underline; }
        .ete-preview p         { margin-bottom: 0.75rem; }
        .ete-preview a         { color: #2563eb; text-decoration: underline; }
        .ete-preview ul        { padding-left: 1.5rem; list-style: disc; margin-bottom: 0.75rem; }
        .ete-preview ol        { padding-left: 1.5rem; list-style: decimal; margin-bottom: 0.75rem; }
        .ete-preview h1        { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem; }
        .ete-preview h2        { font-size: 1.2rem; font-weight: 700; margin-bottom: 0.5rem; }
        .ete-preview h3        { font-size: 1rem; font-weight: 600; margin-bottom: 0.5rem; }
      `}</style>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        {/* ── EDITOR TAB ──────────────────────────────────────────────────── */}
        <Tab eventKey="editor" title="Editor">
          {/* Subject */}
          <div className="mb-3">
            <Form.Label className="fw-semibold mb-1">Email Subject</Form.Label>
            <Form.Control
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="fw-bold fs-5"
            />
          </div>

          {/* Toolbar */}
          <div className="bg-white border border-bottom-0 rounded-top p-2 d-flex flex-wrap align-items-center">
            {/* Font size */}
            <Form.Select
              size="sm"
              style={{ width: "auto", fontSize: 12 }}
              className="me-2"
              onChange={(e) => execCmd("fontSize", e.target.value)}
            >
              {[
                ["2", "10px"],
                ["3", "12px"],
                ["4", "14px"],
                ["5", "18px"],
                ["6", "24px"],
                ["7", "36px"],
              ].map(([v, lbl]) => (
                <option key={v} value={v}>
                  {lbl}
                </option>
              ))}
            </Form.Select>

            <div className="vr mx-1" />

            <FmtBtn
              active={activeFormats.bold}
              onClick={() => execCmd("bold")}
              title="Bold"
            >
              <b>B</b>
            </FmtBtn>
            <FmtBtn
              active={activeFormats.italic}
              onClick={() => execCmd("italic")}
              title="Italic"
            >
              <i>I</i>
            </FmtBtn>
            <FmtBtn
              active={activeFormats.underline}
              onClick={() => execCmd("underline")}
              title="Underline"
            >
              <u>U</u>
            </FmtBtn>
            <FmtBtn
              active={activeFormats.strikeThrough}
              onClick={() => execCmd("strikeThrough")}
              title="Strikethrough"
            >
              <s>S</s>
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              onClick={() => execCmd("formatBlock", "h1")}
              title="Heading 1"
            >
              <span className="fw-bold" style={{ fontSize: 12 }}>
                H1
              </span>
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("formatBlock", "h2")}
              title="Heading 2"
            >
              <span className="fw-bold" style={{ fontSize: 12 }}>
                H2
              </span>
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("formatBlock", "h3")}
              title="Heading 3"
            >
              <span className="fw-bold" style={{ fontSize: 12 }}>
                H3
              </span>
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("formatBlock", "p")}
              title="Paragraph"
            >
              <span style={{ fontSize: 12 }}>¶</span>
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              active={activeFormats.justifyLeft}
              onClick={() => execCmd("justifyLeft")}
              title="Align left"
            >
              ≡
            </FmtBtn>
            <FmtBtn
              active={activeFormats.justifyCenter}
              onClick={() => execCmd("justifyCenter")}
              title="Align center"
            >
              ⋮
            </FmtBtn>
            <FmtBtn
              active={activeFormats.justifyRight}
              onClick={() => execCmd("justifyRight")}
              title="Align right"
            >
              ≡
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              active={activeFormats.insertUnorderedList}
              onClick={() => execCmd("insertUnorderedList")}
              title="Bullet list"
            >
              • —
            </FmtBtn>
            <FmtBtn
              active={activeFormats.insertOrderedList}
              onClick={() => execCmd("insertOrderedList")}
              title="Numbered list"
            >
              1.
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              onClick={() => {
                const url = prompt("Enter URL:", "https://");
                if (url) execCmd("createLink", url);
              }}
              title="Insert link"
            >
              🔗
            </FmtBtn>
            <FmtBtn onClick={() => execCmd("unlink")} title="Remove link">
              🔗̶
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn onClick={() => execCmd("undo")} title="Undo">
              ↩
            </FmtBtn>
            <FmtBtn onClick={() => execCmd("redo")} title="Redo">
              ↪
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("removeFormat")}
              title="Clear formatting"
            >
              ✕
            </FmtBtn>
          </div>

          {/* Editable area */}
          <div
            id="ete-editor"
            ref={initEditor}
            contentEditable
            suppressContentEditableWarning
            spellCheck
            onMouseUp={saveSelection}
            onKeyUp={saveSelection}
            className="border rounded-bottom p-3"
            style={{
              fontSize: 14,
              lineHeight: 1.8,
              outline: "none",
              minHeight: 320,
            }}
          />

          <div className="d-flex justify-content-end mt-3">
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? "Resending..." : "Resend"}
            </Button>{" "}
          </div>
        </Tab>

        {/* ── PREVIEW TAB ─────────────────────────────────────────────────── */}
        <Tab eventKey="preview" title="Preview">
          <div className="border rounded p-4 bg-white">
            <p
              className="text-uppercase text-muted fw-semibold mb-3"
              style={{ letterSpacing: "0.05em", fontSize: 11 }}
            >
              Preview
            </p>

            {/* Subject preview */}
            <div
              className="mb-3 pb-2 fw-bold fs-5"
              style={{ borderBottom: "2px solid #2563eb" }}
            >
              {/* {subject || (
                <span className="text-muted fst-italic fw-normal">
                  No subject
                </span>
              )} */}
            </div>

            {/* Body preview */}
            <div
              className="ete-preview"
              style={{ fontSize: 14, lineHeight: 1.8, color: "#111" }}
              dangerouslySetInnerHTML={{
                __html: activeTab === "preview" ? getPreviewHtml() : "",
              }}
            />
          </div>

          <div className="d-flex justify-content-end mt-3">
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? "Resending..." : "Resend"}
            </Button>
          </div>
        </Tab>
      </Tabs>
    </>
  );
}
